import React, { useState } from 'react';
import { useRecipe, RecipeData, RecipeVariant } from '../../context/RecipeContext';
import { useAuth } from '../../context/AuthContext';
import styles from './RecipeVariantForm.module.css';
import ConfirmationDialog from '../ConfirmationDialog';

interface VariantIngredientInput {
  name: string;
  quantity: string;
  unit: string;
  order: number;
  base_ingredient_id?: string | null;
}

interface VariantInstructionInput {
  text: string;
  order: number;
  base_instruction_id?: string | null;
}

interface RecipeVariantFormProps {
  mode: 'create' | 'edit';
  baseRecipe: RecipeData;
  existingVariant?: RecipeVariant;
  onClose: () => void;
  onSuccess: (variant: RecipeVariant) => void;
}

const RecipeVariantForm: React.FC<RecipeVariantFormProps> = ({ 
  mode, 
  baseRecipe, 
  existingVariant, 
  onClose, 
  onSuccess 
}) => {
  const { createVariantFromRecipe, updateVariant, deleteVariant } = useRecipe();
  const { user } = useAuth();
  
  const [title, setTitle] = useState(
    existingVariant?.title || `My ${baseRecipe.title}`
  );
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  console.log('existingVariant', existingVariant);
  
  const [ingredients, setIngredients] = useState<VariantIngredientInput[]>(() => {
    if (existingVariant) {
      return existingVariant.variant_ingredients.map(ing => ({
        name: ing.name || '',
        quantity: ing.quantity || '',
        unit: ing.unit || '',
        order: ing.order,
        base_ingredient_id: ing.base_ingredient_id
      }));
    } else {
      // Initialize with base recipe ingredients
      return baseRecipe.ingredients.map((ing, idx) => ({
        name: ing.name || '',
        quantity: ing.quantity || '',
        unit: ing.unit || '',
        order: idx,
        base_ingredient_id: null // Will be set when we create variant
      }));
    }
  });
  
  const [instructions, setInstructions] = useState<VariantInstructionInput[]>(() => {
    if (existingVariant) {
      return existingVariant.variant_instructions.map(ins => ({
        text: ins.text || '',
        order: ins.order,
        base_instruction_id: ins.base_instruction_id
      }));
    } else {
      // Initialize with base recipe instructions
      return baseRecipe.instructions.map(ins => ({
        text: ins.text || '',
        order: ins.order,
        base_instruction_id: null // Will be set when we create variant
      }));
    }
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleIngredientChange = (idx: number, field: keyof VariantIngredientInput, value: string) => {
    setIngredients(ings => ings.map((ing, i) => 
      i === idx 
        ? { ...ing, [field]: field === 'order' ? Number(value) : value }
        : ing
    ));
  };

  const handleInstructionChange = (idx: number, field: keyof VariantInstructionInput, value: string) => {
    setInstructions(ins => ins.map((inst, i) => 
      i === idx 
        ? { ...inst, [field]: field === 'order' ? Number(value) : value }
        : inst
    ));
  };

  const addIngredientField = () => {
    setIngredients([...ingredients, { 
      name: '', 
      quantity: '', 
      unit: '', 
      order: ingredients.length,
      base_ingredient_id: null // New ingredient
    }]);
  };

  const removeIngredientField = (idx: number) => {
    setIngredients(ings => ings.filter((_, i) => i !== idx));
  };

  const addInstructionField = () => {
    setInstructions([...instructions, { 
      text: '', 
      order: instructions.length + 1,
      base_instruction_id: null // New instruction
    }]);
  };

  const removeInstructionField = (idx: number) => {
    setInstructions(ins => ins.filter((_, i) => i !== idx));
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!existingVariant) return;
    
    setLoading(true);
    try {
      await deleteVariant(existingVariant.id);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete recipe variant');
    } finally {
      setLoading(false);
      setShowDeleteDialog(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      setError('You must be logged in to customize recipes');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      let result: RecipeVariant | null = null;

      if (mode === 'create') {
        // For create mode, use the special endpoint that copies from base recipe
        result = await createVariantFromRecipe(baseRecipe.id, user.id, title);
        
        // If successful and user made changes, update the variant
        if (result) {
          // Check if user made any changes from the original
          const hasChanges = 
            title !== `My ${baseRecipe.title}` ||
            ingredients.some((ing, idx) => {
              const baseIng = baseRecipe.ingredients[idx];
              return !baseIng || 
                     ing.name !== baseIng.name || 
                     ing.quantity !== baseIng.quantity || 
                     ing.unit !== baseIng.unit;
            }) ||
            instructions.some((ins, idx) => {
              const baseIns = baseRecipe.instructions[idx];
              return !baseIns || ins.text !== baseIns.text || ins.order !== baseIns.order;
            });

          if (hasChanges) {
            // Update with user's changes
            result = await updateVariant(result.id, {
              title,
              base_recipe_id: baseRecipe.id,
              user_id: user.id,
              variant_ingredients: ingredients,
              variant_instructions: instructions
            });
          }
        }
      } else if (mode === 'edit' && existingVariant) {
        result = await updateVariant(existingVariant.id, {
          title,
          base_recipe_id: existingVariant.base_recipe_id, // Use the original base recipe ID
          user_id: user.id,
          variant_ingredients: ingredients,
          variant_instructions: instructions
        });
      }

      if (!result) {
        throw new Error(`Failed to ${mode === 'create' ? 'create' : 'update'} variant`);
      }

      onSuccess(result);
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.formContainer}>
        <div className={styles.header}>
          <div className={styles.headerTop}>
            <h2>{mode === 'create' ? 'Customize Recipe' : 'Edit Custom Recipe'}</h2>
            {mode === 'edit' && existingVariant && (
              <button 
                type="button" 
                className={styles.deleteBtn} 
                onClick={handleDeleteClick}
                disabled={loading}
              >
                Delete Recipe
              </button>
            )}
          </div>
          <div className={styles.baseRecipeInfo}>
            <span>Based on: <strong>{baseRecipe.base_recipe_title || baseRecipe.title}</strong></span>
          </div>
        </div>
        
        {loading && (
          <div className={styles.loadingOverlay}>
            <div className={styles.spinner}></div>
            {mode === 'create' ? 'Creating your custom recipe...' : 'Saving changes...'}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className={styles.form}>
          <label className={styles.label}>
            Your Recipe Title
            <input
              className={styles.input}
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder={`My ${baseRecipe.title}`}
              required
            />
          </label>

          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <span>Customize Ingredients</span>
              <button type="button" className={styles.addBtn} onClick={addIngredientField}>
                + Add New
              </button>
            </div>
            {ingredients.map((ing, idx) => (
              <div key={idx} className={styles.ingredientRow}>
                <input
                  className={styles.input}
                  placeholder="Name"
                  value={ing.name}
                  onChange={e => handleIngredientChange(idx, 'name', e.target.value)}
                  required
                />
                <input
                  className={styles.input}
                  placeholder="Quantity"
                  value={ing.quantity}
                  onChange={e => handleIngredientChange(idx, 'quantity', e.target.value)}
                />
                <input
                  className={styles.input}
                  placeholder="Unit"
                  value={ing.unit}
                  onChange={e => handleIngredientChange(idx, 'unit', e.target.value)}
                />
                <button 
                  type="button" 
                  className={styles.removeBtn} 
                  onClick={() => removeIngredientField(idx)} 
                  disabled={ingredients.length === 1}
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <span>Customize Instructions</span>
              <button type="button" className={styles.addBtn} onClick={addInstructionField}>
                + Add New
              </button>
            </div>
            {instructions.map((inst, idx) => (
              <div key={idx} className={styles.instructionRow}>
                <input
                  className={styles.input}
                  type="number"
                  min={1}
                  style={{ width: 60 }}
                  value={inst.order}
                  onChange={e => handleInstructionChange(idx, 'order', e.target.value)}
                  required
                />
                <input
                  className={styles.input}
                  placeholder="Instruction"
                  value={inst.text}
                  onChange={e => handleInstructionChange(idx, 'text', e.target.value)}
                  required
                />
                <button 
                  type="button" 
                  className={styles.removeBtn} 
                  onClick={() => removeInstructionField(idx)} 
                  disabled={instructions.length === 1}
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          {error && <div className={styles.error}>{error}</div>}

          <div className={styles.buttonRow}>
            <button type="submit" className={styles.submitBtn} disabled={loading}>
              {loading ? 'Saving...' : mode === 'create' ? 'Create My Version' : 'Save Changes'}
            </button>
            <button type="button" className={styles.cancelBtn} onClick={onClose} disabled={loading}>
              Cancel
            </button>
          </div>
        </form>
      </div>

      <ConfirmationDialog
        isOpen={showDeleteDialog}
        title="Delete Recipe Variant"
        message="Are you sure you want to delete this recipe variant? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        type="danger"
      />
    </div>
  );
};

export default RecipeVariantForm;